import { memo, useCallback, useMemo, useState } from "react";
import type { MouseEvent } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Separator from "../components/Separator.tsx";
import IconButton from "../components/IconButton.tsx";
import { getFileType } from "../utils/validators.ts";
import ModalConfirmacao2 from "../../components/ModalConfirmacao2/ModalConfirmacao2.tsx";

export interface CarouselFile {
	id: number;
	url: string;
	nome?: string;
	tipo?: string;
	arquivoId?: number;
}

interface MultiImageCarouselProps {
	images: CarouselFile[];
	chunkSize?: number;
	onSelectImage?: (index: number, file: CarouselFile) => void;
	onDeleteImage?: (id: number) => void;
}

const GAP = 15;
const slideVariants = {
	enter: (d: number) => ({ x: d > 0 ? 300 : -300, opacity: 0 }),
	center: { x: 0, opacity: 1 },
	exit: (d: number) => ({ x: d > 0 ? -300 : 300, opacity: 0 }),
};

function MultiImageCarousel({
	images,
	chunkSize = 5,
	onSelectImage = () => {},
	onDeleteImage = () => {},
}: MultiImageCarouselProps) {
	const chunks = useMemo(() => {
		const r: CarouselFile[][] = [];
		for (let i = 0; i < images.length; i += chunkSize) {
			r.push(images.slice(i, i + chunkSize));
		}
		return r;
	}, [images, chunkSize]);

	const [idx, setIdx] = useState(0);
	const [dir, setDir] = useState(0);
	const [selectedId, setSelectedId] = useState(0);

	const next = useCallback(() => {
		if (idx < chunks.length - 1) {
			setDir(1);
			setIdx((i) => i + 1);
		}
	}, [idx, chunks.length]);

	const prev = useCallback(() => {
		if (idx > 0) {
			setDir(-1);
			setIdx((i) => i - 1);
		}
	}, [idx]);

	const resolveType = useCallback((file: CarouselFile) => {
		if (file.tipo) {
			if (file.tipo.startsWith("image/")) return "image";
			if (file.tipo === "application/pdf") return "pdf";
		}

		if (file.nome) return getFileType(file.nome);

		return getFileType(file.url);
	}, []);

	const handleDownload = useCallback((url: string, e: MouseEvent) => {
		e.stopPropagation();
		e.preventDefault();
		window.open(url, "_blank");
	}, []);

	if (!chunks.length) return null;

	const cur = chunks[idx];
	const itemW = `calc((100% - ${(cur.length - 1) * GAP}px) / ${cur.length})`;

	return (
		<motion.div
			layout
			className="multi-carousel-container"
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
			transition={{ duration: 0.3 }}
			style={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				width: "100%",
				height: "300px",
			}}
		>
			<IconButton
				backgroundColor="#878787"
				icon="ChevronLeft"
				onClick={prev}
				size="lg"
				disabled={idx === 0}
			/>
			<Separator direction="horizontal" size="md" />

			<div
				style={{
					flex: 1,
					position: "relative",
					height: "100%",
					borderRadius: 8,
				}}
			>
				<AnimatePresence custom={dir} initial={false}>
					<motion.div
						layout
						key={idx}
						custom={dir}
						variants={slideVariants}
						initial="enter"
						animate="center"
						exit="exit"
						className="multi-carousel-images"
						transition={{
							x: { type: "spring", stiffness: 200, damping: 30 },
							opacity: { duration: 0.2 },
						}}
						style={{
							position: "absolute",
							inset: 0,
							display: "flex",
							gap: GAP,
							height: "300px",
						}}
					>
						{cur.map((file, i) => {
							const globalIndex = idx * chunkSize + i;
							const kind = resolveType(file);

							return (
								<motion.div
									layout
									key={file.url}
									style={{
										width: itemW,
										position: "relative",
										maxWidth: "300px",
										maxHeight: "300px",
										borderRadius: 8,
									}}
									onClick={() => onSelectImage(globalIndex, file)}
								>
									{kind === "image" && (
										<motion.img
											key={file.url + "image"}
											whileHover={{ scale: 1.05 }}
											whileTap={{ scale: 0.95 }}
											src={file.url}
											alt={file.nome ?? `Imagem ${globalIndex}`}
											style={{
												width: "100%",
												height: "100%",
												objectFit: "cover",
												borderRadius: 8,
												cursor: "pointer",
											}}
										/>
									)}

									{kind === "pdf" && (
										<embed
											key={file.url + "pdf"}
											src={file.url}
											type={file.tipo ?? "application/pdf"}
											width="100%"
											height="100%"
											style={{
												borderRadius: 8,
												cursor: "pointer",
											}}
										/>
									)}

									<div
										style={{
											position: "absolute",
											top: 8,
											right: 8,
											zIndex: 2,
											display: "flex",
											alignItems: "center",
										}}
									>
										<IconButton
											backgroundColor="#fff"
											hoverColor="#f0f0f0"
											theme="dark"
											icon="Download"
											size="sm"
											onClick={(e) => handleDownload(file.url, e)}
										/>
										<Separator direction="horizontal" size="sm" />
										<IconButton
											backgroundColor="#fff"
											hoverColor="#f0f0f0"
											theme="dark"
											icon="Trash"
											size="sm"
											onClick={(e) => {
												e.stopPropagation();
												e.preventDefault();
												setSelectedId(file.id);
											}}
										/>
									</div>
								</motion.div>
							);
						})}
					</motion.div>
				</AnimatePresence>
			</div>

			<Separator direction="horizontal" size="md" />
			<IconButton
				backgroundColor="#878787"
				icon="ChevronRight"
				onClick={next}
				size="lg"
				disabled={idx === chunks.length - 1}
			/>

			<ModalConfirmacao2
				titulo="Deletar Arquivo"
				texto="Tem certeza que deseja deletar este arquivo?"
				textoBotao="Sim"
				open={!!selectedId}
				onClose={() => setSelectedId(0)}
				onClick={() => {
					onDeleteImage(selectedId);
					setSelectedId(0);
				}}
			/>
		</motion.div>
	);
}

export default memo(MultiImageCarousel);
